import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/use-auth';
import type { UserRole } from '../../auth/types';

/**
 * RequireRole guard - wraps routes that require a specific role or higher.
 *
 * Composes with RequireAuth: a route protected by RequireRole should already
 * be inside a RequireAuth subtree, so by the time RequireRole runs we expect
 * auth.status === 'ready' and a profile to be loaded. We re-check anyway for
 * defense-in-depth.
 *
 * Role hierarchy (Stream 0):
 *   super_admin > admin > user
 * A user with super_admin can access admin or user routes.
 * A user with admin can access user routes.
 * A user with user cannot access admin or super_admin routes.
 *
 * Insufficient role redirects to /unauthorized (rendered as a brand-styled
 * landing page that explains the denial; see Unauthorized.tsx).
 */
const ROLE_RANK: Record<UserRole, number> = {
  user: 1,
  admin: 2,
  super_admin: 3,
};

function hasSufficientRole(actual: UserRole, required: UserRole): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

export function RequireRole({
  required,
  children,
}: {
  required: UserRole;
  children: ReactNode;
}) {
  const { auth } = useAuth();

  if (auth.status === 'loading') {
    return null;
  }

  if (auth.status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  if (auth.status === 'unprovisioned') {
    // Same fail-closed treatment as RequireAuth. AuthGate routes
    // unprovisioned users away from authenticated paths.
    return null;
  }

  if (!hasSufficientRole(auth.profile.role, required)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
